import { Injectable } from '@angular/core';
import {
  asyncScheduler,
  forkJoin,
  from,
  Observable,
  scheduled,
  Subject,
} from 'rxjs';
import { GymReportDownload } from '../model/csv-download-models/gym-report-donwload';
import { UploadGym } from '../model/csv-upload-models/upload-gym';
import { UploadLead } from '../model/csv-upload-models/upload-lead';
import { filter, map, reduce, toArray, mergeMap } from 'rxjs/operators';
import { CSVService } from './csv.service';
import { ReportProgress } from '../model/csv-download-models/report-progress';

@Injectable({
  providedIn: 'root',
})
export class LeadReportService {
  constructor(private csvService: CSVService) {}

  public generateReport(
    gymFile: File,
    leadFile: File,
    progressSubject: Subject<ReportProgress>
  ): Observable<GymReportDownload[]> {
    progressSubject.next({ message: 'Loading Files' });

    const gymsObservable = this.csvService.convertCSVFileToObject(
      gymFile,
      Object.keys(new UploadGym()),
      UploadGym
    );
    const leadsObservable = this.csvService.convertCSVFileToObject(
      leadFile,
      Object.keys(new UploadLead()),
      UploadLead
    );

    return forkJoin([gymsObservable, leadsObservable]).pipe(
      mergeMap(([gymData, leadData]): Observable<GymReportDownload[]> => {
        progressSubject.next({
          message:
            'While reports are processing do not navigate away from the page',
        });

        return scheduled(leadData, asyncScheduler).pipe(
          filter(
            lead => lead.id !== null && lead.lat !== null && lead.lng !== null
          ),
          mergeMap((lead, index) => {
            const gymReport = this.numberGymsInRadius(
              [3, 5, 20],
              lead,
              gymData
            );

            progressSubject.next({
              message:
                'Please wait: Processing lead ' +
                index +
                ' of ' +
                leadData.length,
              progress: Math.floor((100 / leadData.length) * (index + 1)),
            });
            return gymReport;
          }),
          toArray<GymReportDownload>()
        );
      })
    );
  }

  private numberGymsInRadius(
    radi: number[],
    lead: UploadLead,
    gymData: UploadGym[]
  ): Observable<GymReportDownload> {
    const gymReport = new GymReportDownload(lead.id);

    return from(gymData).pipe(
      map(gd => this.getDistanceFromLatLngInKm(lead, gd)),
      filter(d => !!d),
      reduce((acc: number[], d: number | null): number[] => {
        radi.forEach((radius, index) => {
          if (d && d < radius) {
            acc[index]++;
          }
        });
        return acc;
      }, new Array<number>(radi.length).fill(0)),
      map(acc => {
        gymReport.radius3km = acc[0];
        gymReport.radius5km = acc[1];
        gymReport.radius20km = acc[2];
        return gymReport;
      })
    );
  }

  private getDistanceFromLatLngInKm(point1: UploadLead, point2: UploadGym) {
    if (
      point1.lat !== null &&
      point1.lng !== null &&
      point2.lat !== null &&
      point2.lng !== null
    ) {
      const radlat1 = (Math.PI * point1.lat) / 180;
      const radlat2 = (Math.PI * point2.lat) / 180;
      const theta = point1.lng - point2.lng;
      const radtheta = (Math.PI * theta) / 180;
      let dist =
        Math.sin(radlat1) * Math.sin(radlat2) +
        Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
      if (dist > 1) {
        dist = 1;
      }
      dist = Math.acos(dist);
      dist = (dist * 180) / Math.PI;
      dist = dist * 60 * 1.1515;
      return dist * 1.609344;
    }
    return null;
  }
}
